import React from 'react';
import type { PaginationRenderItemParams } from '@mui/material/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Pagination,
  PaginationItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Undo, Warning } from '@mui/icons-material';
import { useTranslation } from '@/core/i18n/i18n';
import { appendScopeToApiUrl } from '@/lib/queryClient';
import RegisterEditIcon from '@/static/icons/register-edit.svg?url';
import ComplianceUploadIcon from '@/static/icons/compliance-upload.svg?url';
import ReOpenIcon from '@/static/icons/re-open.svg?url';
import SeeIcon from '@/static/icons/see.svg?url';
import TrashIcon from '@/static/icons/trash.svg?url';
import PDFDocumentIcon from '@/static/icons/PDFdocument.svg?url';
import ProcessIcon from '@/static/icons/process.svg?url';
import { useNavigate } from 'react-router-dom';
import AnularIcon from '@/static/icons/icon-anular-w.svg';
import AutoevaluacionIcon from '@/static/icons/autoevaluacion.svg';

import {
  FilterOptions,
  Plan,
  PLAN_STAGES,
  PLAN_VERSION_STATES,
  AP_PERMISSIONS,
} from '@shared/schema';
import { format } from 'date-fns';
import { AnnualPlanningPeriodState, getPlanActionAvailability } from './availabilityRules';
import ReopenPlanDialog, { ReopenTarget } from './Shared/ReopenPlanDialog';

const HEADER_BG = '#DEEAEB';
const HEADER_BORDER = '#d5e2e7';
const CELL_BORDER = '#e1eaee';

const isDefinitionStage = (stage?: string) =>
  stage === PLAN_STAGES.DEFINITION || stage === PLAN_STAGES.DEFINITION_OUT_OF_TIME;

interface StyledTableProps {
  totalPages: number;
  page: number;
  handleChangePage: (_: React.ChangeEvent<unknown>, value: number) => void;
  paginatedPlans: Array<Plan | null | undefined>;
  tableRows: string[];
  filterOptions: FilterOptions;
  setCurrentVersion: (versionId: number) => void;
  setFulfillmentVersion: (versionId: number) => void;
  setReadOnlyMode: (readOnly: boolean) => void;
  periodState: AnnualPlanningPeriodState;
  setApYear: (year: number | null) => void;
  setCurrentBusinessUnit: (businessUnit: string | null) => void;
  isAnyLoading: boolean;
  deleteAnnualPlan: (id: number) => void;
  setCurrentPlanId: (planId: number | null) => void;
  setSelectedHierarchicalModel: (hierarchicalModelId: number | null) => void;

  reopenPlanVersion?: ((versionId: number, reason: string) => Promise<unknown> | void) | undefined;
  reopenFulfillmentVersion?:
    | ((fulfillmentVersionId: number, reason: string) => Promise<unknown> | void)
    | undefined;
  reloadPage: () => void;
  anularPlanVersion: (apId: number) => void;
  returnPlanToDefinition: (apId: number) => Promise<void> | void;
  roles: string[];
  disableViewButton: boolean;
}

const actionBtn = (bg: string, hover: string, disabledBg: string = '#B0E0E6') => ({
  bgcolor: bg,
  color: '#fff',
  borderRadius: '8px',
  width: 32,
  height: 32,
  p: 0,
  '&:hover': { bgcolor: hover },

  '&.Mui-disabled': {
    bgcolor: disabledBg,
    color: '#B0E0E6',
    opacity: 1,
  },
  '&.Mui-disabled img': {
    filter: 'grayscale(100%)',
    opacity: 0.55,
  },
});

const getStageColor = (stage: string | undefined) => {
  if (isDefinitionStage(stage)) {
    return { bgcolor: '#CEFAFF', border: '1px solid #63E6FD' };
  }
  if (stage === PLAN_STAGES.SELF_ASSESSMENT) {
    return { bgcolor: '#FEFFC2', border: '1px solid #FFE810' };
  }
  return { bgcolor: '#CEFAFF', border: '1px solid #63E6FD' };
};

const getStateColor = (state?: string) => {
  if (!state) {
    return { bg: '#CEFAFF', border: '1px solid #63E6FD' };
  }
  switch (state) {
    case PLAN_VERSION_STATES.IN_PROCESS:
      return { bgcolor: '#FEFFC2', border: '1px solid #FFE810' };
    case PLAN_VERSION_STATES.APPROVED:
      return { bgcolor: '#D8FFEC', border: '1px solid #37F199' };
    case PLAN_VERSION_STATES.REJECTED:
      return { bgcolor: '#FFDDDD', border: '1px solid #FF9595' };
    case PLAN_VERSION_STATES.AWAITING_APPROVAL:
      return { bgcolor: '#FFF4CE', border: '1px solid #FFD24D' };
    default:
      return { bgcolor: '#FEFFC2', border: '1px solid #FFE810' };
  }
};

const StyledTable: React.FC<StyledTableProps> = (props: StyledTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [planToDelete, setPlanToDelete] = React.useState<number | null>(null);

  const [showAnnulModal, setShowAnnulModal] = React.useState(false);
  const [planToAnnul, setPlanToAnnul] = React.useState<number | null>(null);
  const [annulAck, setAnnulAck] = React.useState(false);
  const [showReturnToDefinitionModal, setShowReturnToDefinitionModal] = React.useState(false);
  const [planToReturnToDefinition, setPlanToReturnToDefinition] = React.useState<number | null>(
    null,
  );
  const [showReopenModal, setShowReopenModal] = React.useState(false);

  const [reopenTarget, setReopenTarget] = React.useState<ReopenTarget>(null);

  const {
    totalPages,
    page,
    handleChangePage,
    paginatedPlans,
    tableRows,
    setCurrentVersion,
    setFulfillmentVersion,
    setReadOnlyMode,
    periodState,
    deleteAnnualPlan,
    isAnyLoading,
    setApYear,
    setCurrentBusinessUnit,
    setCurrentPlanId,
    setSelectedHierarchicalModel,
    reopenPlanVersion,
    reopenFulfillmentVersion,
    reloadPage,
    anularPlanVersion,
    returnPlanToDefinition,
    roles,
    disableViewButton,
  } = props;

  const isAdmin = roles.includes(AP_PERMISSIONS.ADMIN);
  const isSelfAssessment = roles.includes(AP_PERMISSIONS.SELF_ASSESSMENT);
  const isCEO = roles.includes(AP_PERMISSIONS.CEO);
  const canView = isAdmin || isCEO || isSelfAssessment;
  const canReopen = isAdmin || isSelfAssessment;
  const canGeneratePdf = isAdmin || isCEO || isSelfAssessment;
  const canDelete = isAdmin;
  const canAnular = isAdmin || isSelfAssessment;
  const visiblePlans = paginatedPlans.filter((plan): plan is Plan => Boolean(plan));

  const handleEditObjectives = (
    planVersionId: number,
    planId: number,
    year: number,
    currentBusinessUnit: string,
    hierarchicalModelId?: number,
  ) => {
    setCurrentVersion(planVersionId);
    setCurrentPlanId(planId);
    if (hierarchicalModelId) setSelectedHierarchicalModel(hierarchicalModelId);
    setReadOnlyMode(false);
    setApYear(year);
    setCurrentBusinessUnit(currentBusinessUnit);
    navigate('/define-objectives');
  };

  const handleAproveDefinition = (
    planVersionId: number,
    _planId: number,
    year: number,
    currentBusinessUnit: string,
  ) => {
    setCurrentVersion(planVersionId);
    setApYear(year);
    setCurrentBusinessUnit(currentBusinessUnit);
    setReadOnlyMode(false);
    navigate('/approval-definitions');
  };

  const handleApproveFulfillment = (
    planVersionId: number,
    fulfillmentVersionId: number,
    _planId: number,
    year: number,
    currentBusinessUnit: string,
  ) => {
    setFulfillmentVersion(fulfillmentVersionId);
    setCurrentVersion(planVersionId);
    setReadOnlyMode(false);
    setApYear(year);
    setCurrentBusinessUnit(currentBusinessUnit);
    navigate('/ceo-validation');
  };

  const handleUploadCompliance = (
    planVersionId: number,
    fulfillmentId: number | null,
    _planId: number,
    year: number,
    currentBusinessUnit: string,
  ) => {
    setCurrentVersion(planVersionId);
    if (fulfillmentId != null) {
      setFulfillmentVersion(fulfillmentId);
    }
    setCurrentBusinessUnit(currentBusinessUnit);
    setReadOnlyMode(false);
    setApYear(year);
    navigate('/enter-compliance');
  };

  const handleViewPlan = (
    planVersionId: number,
    year: number,
    planId: number,
    currentBusinessUnit: string,
    fulfillmentId?: number,
    hierarchicalModelId?: number,
  ) => {
    setCurrentPlanId(planId);
    if (hierarchicalModelId) setSelectedHierarchicalModel(hierarchicalModelId);
    setCurrentVersion(planVersionId);
    setReadOnlyMode(true);
    setCurrentBusinessUnit(currentBusinessUnit);
    setApYear(year);
    if (fulfillmentId) {
      setFulfillmentVersion(fulfillmentId);
      navigate('/enter-compliance');
    } else {
      navigate('/define-objectives');
    }
  };

  const translateHierchicalModel = (id: number | undefined) => {
    if (id === undefined) return '';
    return (
      props.filterOptions?.hierarchicalModels.find((hm) => hm.HierarchicalModelId === id)
        ?.Designation ?? ''
    );
  };
  const handleAnularPlan = (planId: number) => {
    setPlanToAnnul(planId);
    setAnnulAck(false);
    setShowAnnulModal(true);
  };

  const handleReturnToDefinition = (planId: number) => {
    setPlanToReturnToDefinition(planId);
    setShowReturnToDefinitionModal(true);
  };

  const handleDeletePlan = (planId: number) => {
    setPlanToDelete(planId);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (planToDelete) {
      deleteAnnualPlan(planToDelete);
      setShowConfirmModal(false);
      setPlanToDelete(null);
    }
  };

  const getStageName = (stage: string | undefined) => {
    switch (stage) {
      case PLAN_STAGES.DEFINITION:
      case PLAN_STAGES.DEFINITION_OUT_OF_TIME:
        return t('common.definition');
      case PLAN_STAGES.SELF_ASSESSMENT:
        return t('common.self_assessment');
      default:
        return t('common.definition');
    }
  };

  const getStateName = (state: string | undefined) => {
    switch (state) {
      case PLAN_VERSION_STATES.IN_PROCESS:
        return t('common.inProcess');
      case PLAN_VERSION_STATES.APPROVED:
        return t('common.approved');
      case PLAN_VERSION_STATES.REJECTED:
        return t('common.rejected');
      case PLAN_VERSION_STATES.AWAITING_APPROVAL:
        return t('common.awaitingApproval');
      default:
        return t('common.inProcess');
    }
  };

  const handleGeneratePlanPdf = (
    planVersionId?: number,
    fulfillmentVersionId?: number | null,
    status?: string,
  ) => {
    const isSelfAssessment = status === PLAN_STAGES.SELF_ASSESSMENT && !!fulfillmentVersionId;
    if (!planVersionId && !isSelfAssessment) return;
    if (isSelfAssessment && !fulfillmentVersionId) return;

    const cacheBust = `t=${Date.now()}`;
    const url = appendScopeToApiUrl(
      isSelfAssessment
        ? `/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/pdf?${cacheBust}`
        : `/api/annual-plans/${planVersionId}/pdf?${cacheBust}`,
    );

    const win = window.open(url, '_blank', 'noopener');
    if (!win) {
      fetch(url, { headers: { Accept: 'application/pdf' } })
        .then(async (r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          const blob = await r.blob();
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = isSelfAssessment
            ? `annual_plan_fulfillment_${fulfillmentVersionId}.pdf`
            : `annual_plan_${planVersionId}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(blobUrl);
        })
        .catch((err) => console.error('Error descargando PDF del plan:', err));
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 1,
          p: 1,
          overflow: 'hidden',
          mb: 2,
          border: '1px solid #A5B2BB',
        }}
      >
        <TableContainer>
          <Table size="medium">
            {/* CABECERA con “cajitas” y relieve */}
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                {tableRows.map((label, idx) => (
                  <TableCell
                    key={label}
                    align={idx === 6 ? 'center' : 'left'}
                    sx={{
                      p: 0.25,
                      borderBottom: 'none',
                      bgcolor: 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: HEADER_BG,
                        border: `1px solid ${HEADER_BORDER}`,
                        px: 1,
                        py: 1,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        boxShadow:
                          'inset 0 1px 0 rgba(255,255,255,0.85), 0 1px 2px rgba(0,0,0,0.06)',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {/* CUERPO: cada celda con margen y relieve */}
            <TableBody>
              {visiblePlans.map((plan, index) => {
                const rowBg = index % 2 === 0 ? '#EFF5F5' : '#FAFCFC';
                const isLastRow = index === visiblePlans.length - 1;
                const planVersionId = plan?.APVersionId ?? null;
                const hasDefinitionVersion = planVersionId != null;
                const fulfillmentVersionId = plan?.APFulfillmentsVersionId ?? null;
                const hasFulfillmentVersion = fulfillmentVersionId != null;
                const definitionState = plan?.APVersionState;
                const fulfillmentState = plan?.APFulfillmentsVersionState;
                const definitionAwaiting =
                  definitionState === PLAN_VERSION_STATES.AWAITING_APPROVAL;
                const definitionInProcess = definitionState === PLAN_VERSION_STATES.IN_PROCESS;
                const fulfillmentAwaiting =
                  fulfillmentState === PLAN_VERSION_STATES.AWAITING_APPROVAL;
                const fulfillmentInProcess = fulfillmentState === PLAN_VERSION_STATES.IN_PROCESS;
                const {
                  canEditDefinition,
                  canValidateDefinition,
                  canHandleDefinition,
                  canStartSelfAssessment,
                  canEditSelfAssessment,
                  canValidateSelfAssessment,
                  canHandleSelfAssessment,
                  canReopenDefinitionStage,
                  canReopenSelfStage,
                  canShowReopen,
                  canAnnulPlan,
                  canReturnToDefinition,
                } = getPlanActionAvailability({
                  plan,
                  roles,
                  periodState,
                });
                const innerCommon = {
                  border: `1px solid ${CELL_BORDER}`,
                  px: 1,
                  py: 1,
                  bgcolor: rowBg,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 1px rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 48,
                  ...(isLastRow && {
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                  }),
                } as const;
                const definitionActionLabel = definitionAwaiting
                  ? t('table.approveDefinitions')
                  : t('table.enterDefinitions');
                const selfAssessmentActionLabel = fulfillmentAwaiting
                  ? t('table.approveSelfAssessment')
                  : t('table.uploadCompliance');

                return (
                  <TableRow key={plan?.APId} sx={{ bgcolor: 'transparent' }}>
                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box sx={innerCommon}>{plan?.APId}</Box>
                    </TableCell>

                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box sx={innerCommon}>
                        {
                          props.filterOptions?.hierarchicalModels.find(
                            (hm) => hm.HierarchicalModelId === plan?.HierarchicalModelId,
                          )?.Designation
                        }
                      </Box>
                    </TableCell>

                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box sx={innerCommon}>{plan?.Year}</Box>
                    </TableCell>

                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box sx={{ ...innerCommon, justifyContent: 'center' }}>
                        <Box
                          sx={{
                            ...getStageColor(plan?.Status),
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            fontSize: '0.8125rem',
                            fontWeight: 400,
                            width: 140,
                            flex: '0 0 140px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            borderRadius: 1,
                          }}
                        >
                          <img
                            src={isDefinitionStage(plan?.Status) ? ProcessIcon : AutoevaluacionIcon}
                            alt="ProcessIcon"
                            style={{ width: 20, height: 30 }}
                          />
                          {getStageName(plan?.Status)}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box sx={{ ...innerCommon, justifyContent: 'center' }}>
                        <Box
                          sx={{
                            ...getStateColor(
                              plan?.APFulfillmentsVersionState || plan?.APVersionState,
                            ),
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8125rem',
                            fontWeight: 400,
                            minWidth: 110,
                            textAlign: 'center',
                            height: 30,
                            borderRadius: 1,
                          }}
                        >
                          {getStateName(plan?.APFulfillmentsVersionState || plan?.APVersionState)}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box sx={innerCommon}>{plan?.ResponsibleUser}</Box>
                    </TableCell>

                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box sx={innerCommon}>
                        {(() => {
                          return plan?.Created_at
                            ? format(new Date(plan?.Created_at), 'dd/MM/yy HH:mm')
                            : 'N/A';
                        })()}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                      <Box
                        sx={{
                          ...innerCommon,
                          justifyContent: 'center',
                          gap: 0.25,
                        }}
                      >
                        {(isAdmin || isSelfAssessment || isCEO) && (
                          <Tooltip title={definitionActionLabel}>
                            <IconButton
                              size="small"
                              sx={actionBtn('#00BCE4', '#00BCE4')}
                              onClick={() => {
                                if (!hasDefinitionVersion || !canHandleDefinition) return;
                                if (definitionInProcess && canEditDefinition) {
                                  handleEditObjectives(
                                    planVersionId,
                                    plan?.APId,
                                    plan?.Year,
                                    translateHierchicalModel(plan?.HierarchicalModelId),
                                    plan?.HierarchicalModelId,
                                  );
                                  return;
                                }
                                if (definitionAwaiting && canValidateDefinition) {
                                  handleAproveDefinition(
                                    planVersionId,
                                    plan?.APId,
                                    plan?.Year,
                                    translateHierchicalModel(plan?.HierarchicalModelId),
                                  );
                                }
                              }}
                              disabled={isAnyLoading || !canHandleDefinition}
                            >
                              <img
                                src={RegisterEditIcon}
                                alt="RegisterEditIcon"
                                style={{ width: 25, height: 25 }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canReopen && (
                          <Tooltip title={t('table.reopenPlan')}>
                            <IconButton
                              size="small"
                              sx={actionBtn('#00BCE4', '#64c7e1')}
                              disabled={isAnyLoading || !canShowReopen}
                              onClick={() => {
                                if (isAnyLoading || !canShowReopen) return;
                                if (canReopenDefinitionStage && plan?.APVersionId) {
                                  setReopenTarget({
                                    type: 'definition',
                                    versionId: plan.APVersionId,
                                  });
                                  setShowReopenModal(true);
                                } else if (canReopenSelfStage && plan?.APFulfillmentsVersionId) {
                                  setReopenTarget({
                                    type: 'fulfillment',
                                    versionId: plan.APFulfillmentsVersionId,
                                  });
                                  setShowReopenModal(true);
                                }
                              }}
                            >
                              <img
                                src={ReOpenIcon}
                                alt="ReOpenIcon"
                                style={{ width: 25, height: 25 }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAnular &&
                          (canReturnToDefinition ? (
                            <Tooltip title={t('table.returnToDefinition')}>
                              <IconButton
                                size="small"
                                sx={actionBtn('#00BCE4', '#64c7e1')}
                                disabled={isAnyLoading}
                                onClick={() => {
                                  if (isAnyLoading) return;
                                  handleReturnToDefinition(plan.APId);
                                }}
                              >
                                <Undo aria-label={t('table.returnToDefinition')} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('table.anularPlan')}>
                              <IconButton
                                size="small"
                                sx={actionBtn('#00BCE4', '#64c7e1')}
                                disabled={isAnyLoading || !canAnnulPlan}
                                onClick={() => {
                                  if (isAnyLoading || !canAnnulPlan) return;
                                  handleAnularPlan(plan.APId);
                                }}
                              >
                                <img
                                  src={AnularIcon}
                                  alt="AnularIcon"
                                  style={{ width: 25, height: 25 }}
                                />
                              </IconButton>
                            </Tooltip>
                          ))}
                        {canView && (
                          <Tooltip title={t('table.viewPlan')}>
                            <IconButton
                              size="small"
                              sx={actionBtn('#00BCE4', '#64c7e1')}
                              disabled={isAnyLoading || disableViewButton}
                              onClick={() => {
                                if (isAnyLoading || disableViewButton || !plan?.APVersionId) return;
                                handleViewPlan(
                                  plan?.APVersionId,
                                  plan?.Year,
                                  plan?.APId,
                                  translateHierchicalModel(plan?.HierarchicalModelId),
                                  plan?.APFulfillmentsVersionId,
                                  plan?.HierarchicalModelId,
                                );
                              }}
                            >
                              <img src={SeeIcon} alt="SeeIcon" style={{ width: 25, height: 25 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(isAdmin || isSelfAssessment || isCEO) && (
                          <Tooltip title={selfAssessmentActionLabel}>
                            <IconButton
                              size="small"
                              sx={actionBtn('#00BCE4', '#00BCE4')}
                              disabled={isAnyLoading || !canHandleSelfAssessment}
                              onClick={() => {
                                if (!planVersionId || !canHandleSelfAssessment) return;
                                if (
                                  hasFulfillmentVersion &&
                                  fulfillmentAwaiting &&
                                  canValidateSelfAssessment &&
                                  fulfillmentVersionId
                                ) {
                                  handleApproveFulfillment(
                                    planVersionId,
                                    fulfillmentVersionId,
                                    plan.APId,
                                    plan?.Year,
                                    translateHierchicalModel(plan?.HierarchicalModelId),
                                  );
                                  return;
                                }
                                if (canEditSelfAssessment || canStartSelfAssessment) {
                                  handleUploadCompliance(
                                    planVersionId,
                                    fulfillmentVersionId ?? null,
                                    plan.APId,
                                    plan?.Year,
                                    translateHierchicalModel(plan?.HierarchicalModelId),
                                  );
                                }
                              }}
                            >
                              <img
                                src={ComplianceUploadIcon}
                                alt="ComplianceUploadIcon"
                                style={{ width: 25, height: 25 }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canGeneratePdf && (
                          <Tooltip title={t('table.generatePDF')}>
                            <IconButton
                              size="small"
                              sx={actionBtn('#00BCE4', '#64c7e1')}
                              disabled={
                                isAnyLoading ||
                                (isDefinitionStage(plan?.Status)
                                  ? !plan?.APVersionId
                                  : !plan?.APFulfillmentsVersionId)
                              }
                              onClick={() =>
                                handleGeneratePlanPdf(
                                  plan?.APVersionId,
                                  plan?.APFulfillmentsVersionId,
                                  plan?.Status,
                                )
                              }
                            >
                              <img
                                src={PDFDocumentIcon}
                                alt="PDFDocumentIcon"
                                style={{ width: 25, height: 25 }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title={t('table.deletePlan')}>
                            <IconButton
                              size="small"
                              sx={actionBtn('#CC0000', '#d32f2f')}
                              disabled={isAnyLoading}
                              onClick={() => handleDeletePlan(plan.APId)}
                            >
                              <img
                                src={TrashIcon}
                                alt="TrashIcon"
                                style={{ width: 25, height: 25 }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Paginación con "Anterior/Siguiente" */}
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        <Grid size="auto">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            disabled={isAnyLoading || totalPages === 0}
            renderItem={(item: PaginationRenderItemParams) => {
              const spread = item as any;
              return (
                <PaginationItem
                  slots={{
                    previous: () => <span>{t('common.previous')}</span>,
                    next: () => <span>{t('common.next')}</span>,
                  }}
                  {...spread}
                  sx={{
                    minWidth: 36,
                    height: 32,
                    bgcolor: item.selected ? '#e3f4f8' : '#fff',
                    border: '1px solid #d0d7de',
                    '&:hover': { bgcolor: '#f3f6f8' },
                  }}
                />
              );
            }}
          />
        </Grid>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 0,
            pt: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('table.confirmDeleteTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Warning
              sx={{
                fontSize: 64,
                color: '#ffa726',
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: '#333',
              }}
            >
              {t('table.confirmDeleteMessage')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                lineHeight: 1.6,
                maxWidth: '80%',
              }}
            >
              {t('table.confirmDeleteWarning')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 3,
            px: 4,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            size="large"
            disabled={isAnyLoading}
            onClick={() => setShowConfirmModal(false)}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isAnyLoading}
            onClick={handleConfirmDelete}
            sx={{
              bgcolor: '#CC0000',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 150,
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            {t('table.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Annul (Anular) Confirmation Modal */}
      <Dialog
        open={showAnnulModal}
        onClose={() => {
          setShowAnnulModal(false);
          setAnnulAck(false);
          setPlanToAnnul(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('table.confirmAnnulTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Warning sx={{ fontSize: 64, color: '#ffa726', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
              {t('table.confirmAnnulMessage')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, maxWidth: '80%' }}>
              {t('table.confirmAnnulWarning')}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={annulAck}
                  onChange={(e) => setAnnulAck(e.target.checked)}
                  color="primary"
                />
              }
              label={t('table.confirmAnnulAcknowledge')}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 4, gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            disabled={isAnyLoading}
            onClick={() => setShowAnnulModal(false)}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isAnyLoading || planToAnnul == null || !annulAck}
            onClick={() => {
              if (planToAnnul != null) {
                Promise.resolve(anularPlanVersion(planToAnnul)).then(() => {
                  setShowAnnulModal(false);
                  setPlanToAnnul(null);
                  setAnnulAck(false);
                  reloadPage();
                });
              }
            }}
            sx={{
              bgcolor: '#CC0000',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 150,
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            {t('table.confirmAnnul')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return to Definition Confirmation Modal */}
      <Dialog
        open={showReturnToDefinitionModal}
        onClose={() => {
          setShowReturnToDefinitionModal(false);
          setPlanToReturnToDefinition(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('table.confirmReturnToDefinitionTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Warning sx={{ fontSize: 64, color: '#CC0000', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
              {t('table.confirmReturnToDefinitionMessage')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, maxWidth: '80%' }}>
              {t('table.confirmReturnToDefinitionWarning')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 4, gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            disabled={isAnyLoading}
            onClick={() => {
              setShowReturnToDefinitionModal(false);
              setPlanToReturnToDefinition(null);
            }}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isAnyLoading || planToReturnToDefinition == null}
            onClick={() => {
              if (planToReturnToDefinition == null) return;
              Promise.resolve()
                .then(() => returnPlanToDefinition(planToReturnToDefinition))
                .then(() => {
                  setShowReturnToDefinitionModal(false);
                  setPlanToReturnToDefinition(null);
                  reloadPage();
                })
                .catch((error) => {
                  console.error('Error returning plan to definition:', error);
                });
            }}
            sx={{
              bgcolor: '#CC0000',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 180,
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            {t('table.confirmReturnToDefinition')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen Confirmation Modal moved to separate component */}
      <ReopenPlanDialog
        open={showReopenModal}
        onClose={() => {
          setShowReopenModal(false);
          setReopenTarget(null);
        }}
        isAnyLoading={isAnyLoading}
        target={reopenTarget}
        reopenPlanVersion={reopenPlanVersion}
        reopenFulfillmentVersion={reopenFulfillmentVersion}
        reloadPage={reloadPage}
      />
    </>
  );
};

export default StyledTable;
